//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Button, Grid, InputAdornment, InputLabel, TextField, Typography } from '@mui/material'
import {
  commaDecStringToBigNumberWei,
  commaDecStringToErc20Wei,
  commaDecStringToErcDecString,
  erc20WeiToCommaDecString,
  erc20WeiToErc20DecString,
  erc20WeiToNumber,
  toCommaDecString,
} from '@shared/conversions'
import { PoolAccountCTO, PoolOverviewCTO } from '@shared/sharedTypes'
import React, { ReactElement } from 'react'
import ButtonAction from '@react/components/elements/ButtonAction'

import { PoolTypeEnum } from '@shared/Enums'

import { useOtDispatch } from '@react/redux/store'
import { redeemRequestFromPoolTx } from '@react/signedTxs/redeemRequestFromPoolTx'
import { logOT } from '@shared/loggingShared'
import { textFieldValueToBigNumberWei } from '@react/utils/textFieldsUtils'

export default function LenderRequestWithdrawForm(props: {
  poolOverviewCTO: PoolOverviewCTO
  poolAccountCTO: PoolAccountCTO
}): ReactElement {
  const { poolOverviewCTO, poolAccountCTO } = props

  const dispatch = useOtDispatch()

  const exchangeRateBN = BigInt(
    poolOverviewCTO?.activeLoanState.exchangeRateAtMaturity !== '0'
      ? poolOverviewCTO?.activeLoanState.exchangeRateAtMaturity
      : '1000000000000000000'
  )

  const initialUSDCBN = (BigInt(poolAccountCTO?.maxRedeemRequest) * exchangeRateBN) / BigInt(1e18)

  const initialUSDC = Math.round(Number(initialUSDCBN) / 1e4) / 100
  logOT.info('initialUSDCBN', initialUSDCBN, initialUSDC)
  const [valueUSDC, setValueUSDC] = React.useState<string>(initialUSDC.toLocaleString(navigator.language))
  const [valuePPT, setValuePPT] = React.useState<string>(erc20WeiToErc20DecString(poolAccountCTO?.maxRedeemRequest))
  const [errorUSDC, setErrorUSDC] = React.useState<string | undefined>(undefined)
  const [errorPPT, setErrorPPT] = React.useState<string | undefined>(undefined)
  const liquidityTokenSymbol = poolOverviewCTO.liquidityTokenSymbol
  const handleWithdrawRequest = (): void => {
    logOT.debug('LenderRequestWithdrawForm handleWithdrawRequest', valuePPT, valueUSDC)
    dispatch(
      redeemRequestFromPoolTx({
        poolAddr: poolOverviewCTO.poolAddr,
        sharesInWei: commaDecStringToErc20Wei(valuePPT),
        poolType: PoolTypeEnum.Term,
      })
    )
    // }
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }

  const handleCancel = (): void => {
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }

  const errorCheck = (usdc: bigint, ppt: bigint): void => {
    logOT.debug('LenderRequestWithdrawForm SHARES errorCheck', ppt, usdc)

    if (usdc > BigInt(poolAccountCTO?.maxWithdrawRequest)) {
      logOT.debug('LenderRequestWithdrawForm Error ', usdc, erc20WeiToNumber(poolAccountCTO?.maxWithdrawRequest))
      setErrorUSDC(
        `Amount exceeds max withdraw request  ${erc20WeiToCommaDecString(poolAccountCTO?.maxWithdrawRequest)} USDC`
      )
    } else {
      setErrorUSDC(undefined)
    }
    if (ppt > BigInt(poolAccountCTO?.maxRedeemRequest)) {
      logOT.debug('LenderRequestWithdrawForm Error ', ppt, erc20WeiToNumber(poolAccountCTO?.maxRedeemRequest))
      setErrorPPT(
        `Amount exceeds max redemption request  ${erc20WeiToCommaDecString(poolAccountCTO?.maxRedeemRequest)} ${
          poolOverviewCTO.symbol
        }`
      )
    } else {
      setErrorPPT(undefined)
    }
  }

  const handleOnFocusUSDC = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    logOT.debug('handleOnFocus', valueUSDC)

    setValueUSDC(toCommaDecString(valueUSDC))

    const usdc = commaDecStringToBigNumberWei(valueUSDC)
    const ppt = (usdc * BigInt(1e18)) / exchangeRateBN
    setValuePPT(erc20WeiToCommaDecString(ppt))
    errorCheck(usdc, ppt)
  }
  const handleOnBlurUSDC = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    logOT.debug('handleOnBlur1', valueUSDC)
    setValueUSDC(toCommaDecString(valueUSDC))
  }
  const handleOnChangeUSDC = (event: React.ChangeEvent<HTMLInputElement>): void => {
    logOT.debug('handleOnChange', event.target.value)
    setValueUSDC(event.target.value)
    const usdc = textFieldValueToBigNumberWei(event)
    const ppt = (usdc * BigInt(1e18)) / exchangeRateBN

    setValuePPT(erc20WeiToCommaDecString(ppt))
    errorCheck(usdc, ppt)
  }

  const handleOnFocusPPT = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    logOT.debug('handleOnFocusPPT1', valuePPT)
    const ppt = commaDecStringToBigNumberWei(valuePPT)
    setValuePPT(commaDecStringToErcDecString(valuePPT))

    const usdc = (ppt * exchangeRateBN) / BigInt(1e18)
    setValueUSDC(erc20WeiToCommaDecString(usdc))
    errorCheck(usdc, ppt)
  }
  const handleOnBlurPPT = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    logOT.debug('handleOnBlur1', valuePPT)
    setValuePPT(toCommaDecString(valuePPT))
  }
  const handleOnChangePPT = (event: React.ChangeEvent<HTMLInputElement>): void => {
    logOT.debug('handleOnChange', event.target.value)

    setValuePPT(event.target.value)
    const ppt = textFieldValueToBigNumberWei(event)
    const usdc = (ppt * exchangeRateBN) / BigInt(1e18)

    setValueUSDC(erc20WeiToCommaDecString(usdc))
    errorCheck(usdc, ppt)
  }

  const handleMaxClickUSDC = (): void => {
    const maxValue = erc20WeiToCommaDecString(poolAccountCTO?.maxWithdrawRequest)
    setValueUSDC(maxValue)
    const usdc = commaDecStringToBigNumberWei(maxValue)
    const ppt = (usdc * BigInt(1e18)) / exchangeRateBN
    setValuePPT(erc20WeiToCommaDecString(ppt))
    errorCheck(usdc, ppt)
  }

  const _handleMaxClickPPT = (): void => {
    const maxValue = erc20WeiToCommaDecString(poolAccountCTO?.maxRedeemRequest)
    setValuePPT(maxValue)
    const ppt = commaDecStringToBigNumberWei(maxValue)
    const usdc = (ppt * exchangeRateBN) / BigInt(1e18)
    setValueUSDC(erc20WeiToCommaDecString(usdc))
    errorCheck(usdc, ppt)
  }

  const disableButton = !!errorUSDC || !!errorPPT
  logOT.debug('LenderRequestWithdrawForm errorUSDC', disableButton, errorUSDC, errorPPT)

  return (
    <>
      <Box py={2} px={2}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Typography variant="h4">Withdrawal Request</Typography>
            <Typography variant="bodyBold" margin="dense">
              {`${erc20WeiToCommaDecString(
                poolAccountCTO?.maxWithdrawRequest
              )} ${liquidityTokenSymbol} available to withdraw`}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h4">Redeem Request</Typography>
            <Typography variant="bodyBold" margin="dense">
              {`${erc20WeiToCommaDecString(poolAccountCTO?.maxRedeemRequest)} ${
                poolOverviewCTO.symbol
              } available to redeem`}
            </Typography>
          </Grid>

          <Grid>
            <Grid item xs={12}>
              <Typography variant="body" margin={'dense'} sx={{ marginLeft: 6 }}>
                Input the amount you would like to exclude from rollover and withdraw after loan maturity.
              </Typography>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <InputLabel htmlFor="amount">Withdraw Request ({liquidityTokenSymbol})</InputLabel>
            <TextField
              id="amount"
              InputProps={{
                endAdornment: <InputAdornment position="start">{liquidityTokenSymbol}</InputAdornment>,
              }}
              sx={{ width: '300px' }}
              variant="outlined"
              placeholder="Enter Withdraw Amount"
              value={valueUSDC}
              type={'tel'}
              onFocus={handleOnFocusUSDC}
              onChange={handleOnChangeUSDC}
              onBlur={handleOnBlurUSDC}
              error={!!errorUSDC}
              helperText={errorUSDC}
            />
            <Box sx={{ width: '300px', display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
              <Button
                data-testid="button_max"
                color="secondary"
                variant="outlined"
                sx={{
                  // fontSize: '0.75rem', // Smaller font size
                  padding: '4px 8px', // Adjust padding for smaller size
                  textTransform: 'none', // Prevent uppercase transformation
                }}
                onClick={handleMaxClickUSDC}
              >
                Max
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <InputLabel htmlFor="ppt">Withdraw Request ({poolOverviewCTO.symbol})</InputLabel>
            <TextField
              id="ppt"
              InputProps={{
                endAdornment: <InputAdornment position="start">{poolOverviewCTO.symbol}</InputAdornment>,
              }}
              inputProps={{
                'data-testid': 'ppt',
              }}
              sx={{ width: '300px' }}
              variant="outlined"
              placeholder="Enter Withdraw Amount"
              value={valuePPT}
              type={'tel'}
              onFocus={handleOnFocusPPT}
              onChange={handleOnChangePPT}
              onBlur={handleOnBlurPPT}
              error={!!errorPPT}
              helperText={errorPPT}
            />
            <Box sx={{ width: '300px', display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
              <Button
                data-testid="button_max"
                color="secondary"
                variant="outlined"
                sx={{
                  // fontSize: '0.75rem', // Smaller font size
                  padding: '4px 8px', // Adjust padding for smaller size
                  textTransform: 'none', // Prevent uppercase transformation
                }}
                onClick={handleMaxClickUSDC}
              >
                Max
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} sm={12}>
            <ButtonAction
              data-testid={'WithdrawRequest'}
              disabled={disableButton}
              sx={{ marginRight: '16px' }}
              variant={'primary'}
              onClick={handleWithdrawRequest}
            >
              Withdraw Request
            </ButtonAction>

            <ButtonAction variant={'tertiary'} onClick={handleCancel}>
              Cancel
            </ButtonAction>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}
